import { getAccessToken } from '@nexus/supabase';
import { getSubscriptionManagerUrl } from '../config/global';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { optimisticMutation } from '@nexus/ari-lib/hooks/optimistic-mutation';
import { Serial } from '@nexus/common';

export const USER_DEVICES_QUERY_KEY = ['user_devices'] as const;
const FIVE_MINS_IN_MS = 5 * 60 * 1000;
const TEN_MINS_IN_MS = 10 * 60 * 1000;

export type DeviceResponse = {
  serialNumber: string;
  workspace: string | null;
  workspaceShortName: string | null;
  name: string | null;
  deviceModel: string | null;
  firmwareRelease: string | null;
  friendlyModel: string | null;
  model: string | null;
  connected: boolean;
};

type GetUserDevicesResponse = {
  devices: DeviceResponse[];
};

async function getToken(): Promise<string> {
  const token = await getAccessToken();
  if (!token) throw new Error('No authenticated session');
  return token;
}

function getHeaders(token: string) {
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
}

async function getUserDevices(): Promise<DeviceResponse[]> {
  const token = await getToken();
  const response = await fetch(`${getSubscriptionManagerUrl()}/v1/devices/user`, {
    method: 'GET',
    headers: getHeaders(token),
  });
  // user not found errors should be treated as no devices found (the user didn't link any device yet)
  if (!response.ok && response.status === 404) {
    console.error(await response.json());
    return [];
  }
  if (!response.ok) {
    throw new Error(`Get user devices request failed with status ${response.status}`);
  }

  const responseBody = (await response.json()) as GetUserDevicesResponse;
  return responseBody.devices;
}

async function disconnectUserDevice(deviceId: string): Promise<void> {
  const token = await getToken();
  const response = await fetch(`${getSubscriptionManagerUrl()}/v1/devices/${deviceId}`, {
    method: 'DELETE',
    headers: getHeaders(token),
    body: JSON.stringify({}),
  });
  if (!response.ok) {
    throw new Error(`Disconnect user device request failed with status ${response.status}`);
  }
}

export function useDevices() {
  const queryClient = useQueryClient();

  const { data, error, isPending } = useQuery({
    queryKey: USER_DEVICES_QUERY_KEY,
    queryFn: getUserDevices,
    staleTime: FIVE_MINS_IN_MS,
    gcTime: TEN_MINS_IN_MS,
  });

  const userDevices = useMemo(() => (data ? data.map(convertToUiDeviceItem) : []), [data]);

  const disconnectMutation = useMutation(
    optimisticMutation<void, string, DeviceResponse[]>({
      queryClient,
      queryKey: USER_DEVICES_QUERY_KEY,
      mutationFn: (deviceId) => disconnectUserDevice(deviceId),
      updater: (old, deviceId) => {
        return !old ? old : old.filter((device) => device.serialNumber !== deviceId);
      },
    })
  );

  return {
    userDevices,
    error,
    isLoading: isPending,
    disconnectMutation,
  };
}
export type UseDevicesReturn = ReturnType<typeof useDevices>;

export type UiDeviceItem = {
  serialNumber: string;
  name: string | null;
  model: ModelName;
  status: 'ONLINE' | 'OFFLINE';
  firmwareVersion: string | null;
  workspace: string | null;
  workspaceShortName: string | null;
};

function convertToUiDeviceItem(device: DeviceResponse): UiDeviceItem {
  return {
    serialNumber: device.serialNumber,
    name: device.name,
    model: getModel(device),
    status: device.connected ? 'ONLINE' : 'OFFLINE',
    firmwareVersion: getFirmwareVersion(device),
    workspace: device.workspace,
    workspaceShortName: device.workspaceShortName,
  };
}

// this is used as fallback when friendly model doesn't exist
// display names were chosen based on FriendlyModel values from mongo prod
// so the list of possible UI values is as short as possible
const modelRegexWithDisplayName = [
  { exp: Serial.Hmt, displayName: 'HMT-1' },
  { exp: Serial.Navigator, displayName: 'Navigator 5XX' }, // only friendlyModel can tell us if it's 500 or 520
  { exp: Serial.NavigatorZ1, displayName: 'NAVIGATOR-Z1' },
  { exp: Serial.Arc3, displayName: 'ARC 3' },
  { exp: Serial.Vuzix, displayName: 'Vuzix M400' },
  { exp: Serial.Rokid, displayName: 'ROKID GLASSES' },
] as const;

export type ModelName = 'NAV500' | 'NAV520' | typeof modelRegexWithDisplayName[number]['displayName'] | 'UNKNOWN MODEL';

function getModel(device: DeviceResponse): ModelName {
  if (device.friendlyModel) {
    return device.friendlyModel as ModelName;
  }
  if (isVuzixDevice(device)) {
    return device.model as ModelName;
  }
  const matchingRegex = modelRegexWithDisplayName.find((entry) => entry.exp.test(device.serialNumber));
  return !matchingRegex ? 'UNKNOWN MODEL' : matchingRegex.displayName;
}

function getFirmwareVersion(device: DeviceResponse): string | null {
  if (!device.firmwareRelease) {
    return null;
  }
  // special format for Rokid, example: SKQ1.240613.001 release-keys
  if (getModel(device).toLowerCase().includes('rokid')) {
    return device.firmwareRelease.split(' ')[0];
  }
  const [productVersion, buildNumber] = device.firmwareRelease.split('-');
  return buildNumber ? `${productVersion}-${buildNumber}` : productVersion;
}

function isVuzixDevice(device: DeviceResponse): boolean {
  return typeof device.model === 'string' && device.model?.toLowerCase()?.includes('vuzix');
}
